/**
 * DVB Teletext helpers for raw PES / PES-payload BIN / MPEG-TS files
 * (ETSI EN 300 472, ETSI EN 300 706).
 *
 * Timing notes:
 * - Payload-only BINs (no MPEG-TS `0x47`, no PES `00 00 01 BD`) have no PTS/DTS/PCR.
 *   Timing uses a field clock: time = fieldIndex / fieldRate (default 50 Hz).
 * - MPEG-TS files are demuxed to contiguous PES first (`demuxMpegTsToPes`).
 * - When PES private_stream_1 packets with PTS are present, times use (pts - basePts).
 */
export default {
  cntrlCodes: {
    "00": "black",
    "01": "red",
    "02": "green",
    "03": "yellow",
    "04": "blue",
    "05": "magenta",
    "06": "cyan",
    "07": "white"
  },
  toBuffer: function (input) {
    if (Buffer.isBuffer(input)) {
      return input;
    }
    if (input instanceof Uint8Array) {
      return Buffer.from(input);
    }
    if (typeof input === "string") {
      const trimmed = input.replace(/\s+/g, "");
      if (/^[0-9a-fA-F]+$/.test(trimmed) && trimmed.length % 2 === 0) {
        return Buffer.from(trimmed, "hex");
      }
      return Buffer.from(input, "binary");
    }
    throw new Error("tsStream: unsupported input type");
  },
  bitReverse: function (byte) {
    let value = byte & 0xff;
    let result = 0;
    for (let i = 0; i < 8; i++) {
      result = result << 1 | value & 1;
      value >>= 1;
    }
    return result;
  },
  /**
   * ETSI EN 300 706 Hamming 8/4: data bits at even positions (1-indexed bits 2,4,6,8).
   */
  unhamming84: function (byte) {
    return byte >> 1 & 1 | byte >> 2 & 2 | byte >> 3 & 4 | byte >> 4 & 8;
  },
  stripParity: function (byte) {
    return byte & 0x7f;
  },
  /**
   * Extract 33-bit PTS (90 kHz) from PES optional fields starting at ptsOffset.
   * Returns seconds, or null if PTS not present.
   * Note: DTS is not read. PCR may exist in MPEG-TS adaptation fields but is not used here;
   * after demux, timing comes from PES PTS (PCR-fixed by ffmpeg when re-extracting).
   */
  readPtsSeconds: function (buf, ptsOffset) {
    if (ptsOffset + 4 >= buf.length) {
      return null;
    }
    const b0 = buf[ptsOffset];
    const b1 = buf[ptsOffset + 1];
    const b2 = buf[ptsOffset + 2];
    const b3 = buf[ptsOffset + 3];
    const b4 = buf[ptsOffset + 4];
    const pts = (BigInt(b0) & 0x0en) << 29n | BigInt(b1) << 22n | (BigInt(b2) & 0xfen) << 14n | BigInt(b3) << 7n | (BigInt(b4) & 0xfen) >> 1n;
    return Number(pts) / 90000;
  },
  /**
   * True when buffer looks like MPEG-TS (sync `0x47` at packet boundaries).
   */
  isMpegTs: function (buf) {
    return buf.length >= 376 && buf[0] === 0x47 && buf[188] === 0x47;
  },
  /**
   * Demux MPEG-TS into a contiguous PES byte stream for the teletext PID
   * (first PID whose payload starts with private_stream_1 `00 00 01 BD`).
   * Non-TS buffers are returned unchanged.
   */
  demuxMpegTsToPes: function (buf) {
    const PACKET = 188;
    if (!this.isMpegTs(buf)) {
      return buf;
    }
    const getPayloadRange = off => {
      if (buf[off] !== 0x47) {
        return null;
      }
      const pid = (buf[off + 1] & 0x1f) << 8 | buf[off + 2];
      if (pid === 0x1fff) {
        return null;
      }
      const afc = buf[off + 3] >> 4 & 0x03;
      if (afc === 0 || afc === 2) {
        return null;
      }
      let payloadStart = off + 4;
      if (afc === 2 || afc === 3) {
        const afl = buf[off + 4];
        payloadStart = off + 5 + afl;
      }
      const packetEnd = off + PACKET;
      if (payloadStart >= packetEnd) {
        return null;
      }
      return {
        pid,
        payloadStart,
        packetEnd,
        pusi: buf[off + 1] >> 6 & 1
      };
    };
    let targetPid = null;
    for (let off = 0; off + PACKET <= buf.length; off += PACKET) {
      const range = getPayloadRange(off);
      if (!range || !range.pusi) {
        continue;
      }
      const {
        payloadStart,
        packetEnd
      } = range;
      if (payloadStart + 3 < packetEnd && buf[payloadStart] === 0x00 && buf[payloadStart + 1] === 0x00 && buf[payloadStart + 2] === 0x01 && buf[payloadStart + 3] === 0xbd) {
        targetPid = range.pid;
        break;
      }
    }
    if (targetPid === null) {
      return buf;
    }
    const chunks = [];
    let total = 0;
    for (let off = 0; off + PACKET <= buf.length; off += PACKET) {
      const range = getPayloadRange(off);
      if (!range || range.pid !== targetPid) {
        continue;
      }
      const slice = buf.subarray(range.payloadStart, range.packetEnd);
      chunks.push(slice);
      total += slice.length;
    }
    return Buffer.concat(chunks, total);
  },
  /**
   * Find whether the buffer contains any PES private_stream_1 packets (`00 00 01 BD`).
   * False for payload-only dumps (e.g. output.bin from `ffmpeg -c copy -f data`).
   */
  hasPesPackets: function (buf) {
    for (let i = 0; i < buf.length - 3; i++) {
      if (buf[i] === 0x00 && buf[i + 1] === 0x00 && buf[i + 2] === 0x01 && buf[i + 3] === 0xbd) {
        return true;
      }
    }
    return false;
  },
  /**
   * Parse one PES_data_field starting at offset.
   * Returns { nextOffset, units: [{ id, data }] } or null on failure.
   */
  parsePesDataField: function (buf, offset) {
    if (offset >= buf.length) {
      return null;
    }
    let i = offset;
    const di = buf[i];
    if (di < 0x10 || di > 0x1f) {
      return null;
    }
    i++;
    const units = [];
    while (i + 1 < buf.length) {
      const id = buf[i];
      const len = buf[i + 1];
      if (id >= 0x10 && id <= 0x1f && (buf[i + 1] === 0x02 || buf[i + 1] === 0x03 || buf[i + 1] === 0xff) && buf[i + 2] === 0x2c) {
        break;
      }
      if (len !== 0x2c) {
        break;
      }
      if (i + 2 + len > buf.length) {
        break;
      }
      if (id === 0x02 || id === 0x03) {
        units.push({
          id,
          data: buf.subarray(i + 2, i + 2 + len)
        });
      }
      i += 2 + len;
      if (i < buf.length && buf[i] >= 0x10 && buf[i] <= 0x1f && i + 2 < buf.length && (buf[i + 1] === 0x02 || buf[i + 1] === 0x03 || buf[i + 1] === 0xff) && buf[i + 2] === 0x2c) {
        break;
      }
    }
    return {
      nextOffset: i,
      units
    };
  },
  /**
   * Decode a 44-byte teletext data_field into magazine/row/page/control/chars.
   */
  decodeDataField: function (data) {
    if (!data || data.length < 4) {
      return null;
    }
    const lineInfo = data[0];
    const fieldParity = lineInfo >> 5 & 0x01;
    const lineOffset = lineInfo & 0x1f;

    /* Framing code is stored in DVB bit order (0xE4); subsequent bytes need bit-reversal. */
    if (data[1] !== 0xe4) {
      return null;
    }
    const packet = Buffer.alloc(data.length);
    packet[0] = data[0];
    packet[1] = data[1];
    for (let k = 2; k < data.length; k++) {
      packet[k] = this.bitReverse(data[k]);
    }
    const u1 = this.unhamming84(packet[2]);
    const u2 = this.unhamming84(packet[3]);
    let magazine = u1 & 0x07;
    if (magazine === 0) {
      magazine = 8;
    }
    const row = u1 >> 3 & 0x01 | (u2 & 0x0f) << 1;
    const result = {
      fieldParity,
      lineOffset,
      magazine,
      row,
      page: null,
      erase: false,
      subtitle: false,
      newsflash: false,
      chars: []
    };
    if (row === 0) {
      const pageUnits = this.unhamming84(packet[4]);
      const pageTens = this.unhamming84(packet[5]);
      result.page = magazine * 100 + pageTens * 10 + pageUnits;

      /* EN 300 706: after page number, Hamming nibbles carry subcode + C4–C6.
         ham[7] bit3 = C4 (erase); ham[9] bit2 = C5 (newsflash); ham[9] bit3 = C6 (subtitle). */
      const n7 = this.unhamming84(packet[7]);
      const n9 = this.unhamming84(packet[9]);
      result.erase = (n7 & 0x08) !== 0;
      result.newsflash = (n9 & 0x04) !== 0;
      result.subtitle = (n9 & 0x08) !== 0;
    } else if (row >= 1 && row <= 25) {
      for (let c = 4; c < packet.length; c++) {
        result.chars.push(this.stripParity(packet[c]));
      }
    }
    return result;
  },
  /**
   * Convert a row of teletext character codes to display text with color tags.
   * Alpha color codes (0x00–0x07) are spacing attributes: insert a space unless
   * the plain text already ends with whitespace.
   */
  charsToText: function (chars) {
    let text = "";
    let colorOpen = false;
    let colorTag = "";
    const colorNames = "black|red|green|yellow|blue|magenta|cyan|white";
    const plainEndsWithSpace = () => {
      const plain = text.replace(new RegExp(`</?(?:${colorNames})>`, "gi"), "");
      return !plain.length || /\s$/.test(plain);
    };
    const closeColor = () => {
      if (colorOpen) {
        text += `</${colorTag}>`;
        colorOpen = false;
        colorTag = "";
      }
    };
    for (let i = 0; i < chars.length; i++) {
      const ch = chars[i];
      if (ch >= 0x00 && ch <= 0x07) {
        const name = this.cntrlCodes[ch.toString(16).padStart(2, "0")];
        if (name) {
          closeColor();
          if (!plainEndsWithSpace()) {
            text += " ";
          }
          text += `<${name}>`;
          colorTag = name;
          colorOpen = true;
        }
      } else if (ch === 0x0a || ch === 0x0b || ch === 0x0c || ch === 0x0d) {
        /* End box / start box / normal size / double height — skip */
      } else if (ch >= 0x20 && ch < 0x7f) {
        text += String.fromCharCode(ch);
      }
    }
    text = text.replace(/ +$/g, "");
    closeColor();
    return text;
  },
  /**
   * Walk the entire stream and yield decoded teletext packets with timestamps.
   * options: { fieldRate }
   * MPEG-TS is demuxed to contiguous PES first.
   * Raw (no PES) BINs — e.g. samples/bin/output.bin — have no PTS/DTS/PCR:
   * one tick per data_identifier field at fieldRate (fieldIndex / fieldRate).
   * PES with PTS: use (pts - basePts); else fall back to unit index / fieldRate.
   */
  extractPackets: function (input, options = {}) {
    let buf = this.toBuffer(input);
    if (this.isMpegTs(buf)) {
      buf = this.demuxMpegTsToPes(buf);
    }
    const fieldRate = parseFloat(options.fieldRate) || 50;
    const packets = [];
    let unitIndex = 0;
    let basePts = null;
    let usePts = this.hasPesPackets(buf);
    if (usePts) {
      let i = 0;
      while (i < buf.length - 9) {
        if (!(buf[i] === 0x00 && buf[i + 1] === 0x00 && buf[i + 2] === 0x01 && buf[i + 3] === 0xbd)) {
          i++;
          continue;
        }
        const packetLength = buf[i + 4] << 8 | buf[i + 5];
        const flags = buf[i + 7];
        const headerDataLen = buf[i + 8];
        const optionalStart = i + 9;
        const payloadStart = optionalStart + headerDataLen;
        const pesEnd = i + 6 + packetLength;
        let ptsSec = null;
        if ((flags & 0x80) === 0x80) {
          ptsSec = this.readPtsSeconds(buf, optionalStart);
          if (ptsSec !== null && basePts === null) {
            basePts = ptsSec;
          }
        }
        let time = ptsSec !== null && basePts !== null ? ptsSec - basePts : unitIndex / fieldRate;
        let p = payloadStart;
        while (p < Math.min(pesEnd, buf.length)) {
          const field = this.parsePesDataField(buf, p);
          if (!field || field.units.length === 0 && field.nextOffset === p) {
            break;
          }
          for (const unit of field.units) {
            const decoded = this.decodeDataField(unit.data);
            if (decoded) {
              if (ptsSec === null) {
                time = unitIndex / fieldRate;
              }
              packets.push({
                ...decoded,
                time,
                dataUnitId: unit.id
              });
              unitIndex++;
            }
          }
          if (field.nextOffset <= p) {
            break;
          }
          p = field.nextOffset;
        }
        i = pesEnd > i ? pesEnd : i + 1;
      }
    } else {
      /*
       * Concatenated PES payloads (no PTS): each data_identifier (0x10–0x1F)
       * starts one VBI field. Stuffing units (0xFF) belong to that field and
       * must advance the field clock; only 0x02/0x03 are emitted.
       */
      let i = 0;
      let fieldIndex = 0;
      let sawUnitInField = false;
      const isDataIdentifierAt = pos => pos + 2 < buf.length && buf[pos] >= 0x10 && buf[pos] <= 0x1f && (buf[pos + 1] === 0x02 || buf[pos + 1] === 0x03 || buf[pos + 1] === 0xff) && buf[pos + 2] === 0x2c;
      if (isDataIdentifierAt(0)) {
        i = 1;
      }
      while (i < buf.length) {
        if (isDataIdentifierAt(i)) {
          if (sawUnitInField) {
            fieldIndex++;
            sawUnitInField = false;
          }
          i++;
          continue;
        }
        if (i + 1 >= buf.length) {
          break;
        }
        const id = buf[i];
        const len = buf[i + 1];
        if (len !== 0x2c) {
          i++;
          continue;
        }
        if (i + 2 + len > buf.length) {
          break;
        }
        const time = fieldIndex / fieldRate;
        if (id === 0x02 || id === 0x03) {
          const decoded = this.decodeDataField(buf.subarray(i + 2, i + 2 + len));
          if (decoded) {
            packets.push({
              ...decoded,
              time,
              dataUnitId: id
            });
          }
        }
        sawUnitInField = true;
        i += 2 + len;
      }
    }
    return packets;
  },
  /**
   * Normalize page text for coalescing comparisons.
   */
  normalizeText: function (text) {
    return (text || "").replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
  },
  /**
   * Build page display text from row map (row number → raw chars or text).
   */
  rowsToPageText: function (rows) {
    const lineNos = Object.keys(rows).map(Number).sort((a, b) => a - b);
    const lines = [];
    for (const row of lineNos) {
      const line = typeof rows[row] === "string" ? rows[row] : this.charsToText(rows[row]);
      if (line && this.normalizeText(line)) {
        lines.push(line);
      }
    }
    return lines.join("\n");
  },
  /**
   * Plain text with tags removed but newlines preserved (for carry-over strip).
   */
  toPlainMultiline: function (text) {
    return (text || "").replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>\s*<p[^>]*>/gi, "\n").replace(/<\/div>\s*<div[^>]*>/gi, "\n").replace(/<[^>]+>/g, "").replace(/\r\n/g, "\n").replace(/[ \t]+\n/g, "\n").replace(/\n[ \t]+/g, "\n").replace(/\n{2,}/g, "\n").trim();
  },
  /**
   * Strip teletext scroll carry-over: last line / last word of prev that prefixes curr.
   * Returns stripped current plain text (may be empty).
   */
  stripCarryOverPrefix: function (prevPlain, currPlain) {
    if (!prevPlain || !currPlain) {
      return currPlain || "";
    }
    const prevLines = prevPlain.split("\n").map(l => l.trim()).filter(Boolean);
    if (!prevLines.length) {
      return currPlain;
    }
    const lastLine = prevLines[prevLines.length - 1];
    let stripped = null;
    if (lastLine.length >= 2 && currPlain.startsWith(lastLine)) {
      stripped = currPlain.slice(lastLine.length);
    } else {
      const words = lastLine.split(/\s+/).filter(Boolean);
      const lastWord = words[words.length - 1] || "";
      if (lastWord.length >= 2) {
        if (currPlain === lastWord || currPlain.startsWith(lastWord + " ") || currPlain.startsWith(lastWord + "\n")) {
          stripped = currPlain.slice(lastWord.length);
        } else if (currPlain.startsWith(lastWord) && currPlain.length > lastWord.length) {
          const after = currPlain.charAt(lastWord.length);
          /* Glued forms: still.What / wordWord with uppercase continuation */
          if (/[A-Z0-9]/.test(after) || lastWord.endsWith(".") && /[A-Za-z]/.test(after)) {
            stripped = currPlain.slice(lastWord.length);
          }
        }
      }
    }
    if (stripped === null) {
      return currPlain;
    }
    return stripped.replace(/^\s+/, "").trim();
  },
  /**
   * Keep at most maxLines non-empty lines without dropping words.
   * Overflow lines are folded onto the last kept line.
   * Plain-text variant (used for comparisons).
   */
  limitToMaxLines: function (text, maxLines = 2) {
    const lines = this.toPlainMultiline(text).split("\n").map(l => l.trim()).filter(Boolean);
    if (lines.length <= maxLines) {
      return lines.join("\n");
    }
    const kept = lines.slice(0, maxLines - 1);
    const overflow = lines.slice(maxLines - 1).join(" ");
    kept.push(overflow);
    return kept.join("\n");
  },
  /**
   * Remove the first plainPrefixLen plain characters from tagged text, keeping tags.
   */
  removePlainPrefixFromTagged: function (tagged, plainPrefixLen) {
    if (!tagged || plainPrefixLen <= 0) {
      return tagged || "";
    }
    let plainSeen = 0;
    let i = 0;
    const s = tagged;
    while (i < s.length && plainSeen < plainPrefixLen) {
      if (s[i] === "<") {
        const close = s.indexOf(">", i);
        if (close === -1) {
          break;
        }
        i = close + 1;
        continue;
      }
      plainSeen++;
      i++;
    }
    let rest = s.slice(i);
    /* Drop leading empty color tag pairs left after the cut */
    rest = rest.replace(/^(<(?:black|red|green|yellow|blue|magenta|cyan|white)><\/(?:black|red|green|yellow|blue|magenta|cyan|white)>)+/i, "");
    rest = rest.replace(/^<\/(?:black|red|green|yellow|blue|magenta|cyan|white)>/i, "");
    return rest.trim();
  },
  /**
   * Carry-over strip that preserves color tags on the remaining text.
   */
  stripCarryOverTagged: function (prevTagged, currTagged) {
    const prevPlain = this.toPlainMultiline(prevTagged);
    const currPlain = this.toPlainMultiline(currTagged);
    const strippedPlain = this.stripCarryOverPrefix(prevPlain, currPlain);
    if (strippedPlain === currPlain) {
      return currTagged;
    }
    if (!strippedPlain) {
      return "";
    }
    const prefixLen = currPlain.length - strippedPlain.length;
    let result = this.removePlainPrefixFromTagged(currTagged, prefixLen);

    /* If cut left us mid-tag color, ensure a matching open tag from remaining content */
    const plainCheck = this.toPlainMultiline(result);
    if (plainCheck !== strippedPlain && strippedPlain.endsWith(plainCheck)) {
      /* Accept shorter plain if only whitespace/tag normalization differs */
    }
    result = result.replace(/<(black|red|green|yellow|blue|magenta|cyan|white)>\s*<\/\1>/gi, "");
    return result.trim();
  },
  /**
   * Keep at most maxLines tagged lines without dropping words.
   * Overflow lines are folded onto the last kept line.
   */
  limitToMaxLinesTagged: function (tagged, maxLines = 2) {
    const lines = (tagged || "").replace(/\r\n/g, "\n").split("\n").map(l => l.trim()).filter(l => this.toPlainMultiline(l).length > 0);
    if (lines.length <= maxLines) {
      return lines.join("\n");
    }
    const kept = lines.slice(0, maxLines - 1);
    const overflow = lines.slice(maxLines - 1).join(" ");
    kept.push(overflow);
    return kept.join("\n");
  },
  mergeTaggedLines: function (a, b, maxLines = 2) {
    const left = (a || "").trim();
    const right = (b || "").trim();
    if (!left) {
      return this.limitToMaxLinesTagged(right, maxLines);
    }
    if (!right) {
      return this.limitToMaxLinesTagged(left, maxLines);
    }
    return this.limitToMaxLinesTagged(left + "\n" + right, maxLines);
  },
  /**
   * Color of the first plain character, or null if uncolored.
   */
  getLeadingColor: function (tagged) {
    if (!tagged) {
      return null;
    }
    const re = /<\/?(black|red|green|yellow|blue|magenta|cyan|white)>/gi;
    let color = null;
    let i = 0;
    const s = tagged;
    while (i < s.length) {
      if (s[i] === "<") {
        re.lastIndex = i;
        const match = re.exec(s);
        if (match && match.index === i) {
          const name = match[1].toLowerCase();
          if (match[0][1] === "/") {
            if (color === name) {
              color = null;
            }
          } else {
            color = name;
          }
          i = match.index + match[0].length;
          continue;
        }
      }
      if (s[i] === "\n" || s[i] === "\r" || s[i] === " ") {
        i++;
        continue;
      }
      if (s[i] !== "<") {
        return color;
      }
      i++;
    }
    return null;
  },
  /**
   * Color of the last plain character (survives closing tags at end of cue).
   */
  getTrailingColor: function (tagged) {
    if (!tagged) {
      return null;
    }
    const re = /<\/?(black|red|green|yellow|blue|magenta|cyan|white)>/gi;
    let color = null;
    let lastPlainColor = null;
    let i = 0;
    const s = tagged;
    while (i < s.length) {
      if (s[i] === "<") {
        re.lastIndex = i;
        const match = re.exec(s);
        if (match && match.index === i) {
          const name = match[1].toLowerCase();
          if (match[0][1] === "/") {
            if (color === name) {
              color = null;
            }
          } else {
            color = name;
          }
          i = match.index + match[0].length;
          continue;
        }
      }
      if (s[i] !== "<" && s[i] !== "\n" && s[i] !== "\r") {
        if (s[i] !== " ") {
          lastPlainColor = color;
        }
      }
      i++;
    }
    return lastPlainColor;
  },
  /**
   * Wrap uncolored leading content with an inherited color tag.
   */
  applyInheritedColor: function (tagged, color) {
    if (!tagged || !color || this.getLeadingColor(tagged)) {
      return tagged || "";
    }
    if (!this.toPlainMultiline(tagged)) {
      return tagged;
    }
    if (new RegExp(`^\\s*<${color}>`, "i").test(tagged)) {
      return tagged;
    }
    const openRe = /<(black|red|green|yellow|blue|magenta|cyan|white)>/i;
    const match = openRe.exec(tagged);
    if (!match) {
      return `<${color}>${tagged}</${color}>`;
    }
    const before = tagged.slice(0, match.index);
    const after = tagged.slice(match.index);
    if (!this.toPlainMultiline(before)) {
      return tagged;
    }
    return `<${color}>${before}</${color}>${after}`;
  },
  /**
   * Reflow tagged text into at most maxLines, preferring breaks after .?!
   * and respecting maxWidth plain characters per line.
   */
  reflowToMaxLinesTagged: function (tagged, maxLines = 2, maxWidth = 38) {
    if (!tagged || !this.toPlainMultiline(tagged)) {
      return "";
    }
    let flat = tagged.replace(/\r\n/g, "\n").replace(/\n+/g, " ").replace(/[ \t]{2,}/g, " ").trim();
    flat = flat.replace(/([.?!])(?=[A-Z])/g, "$1 ");
    const tokens = [];
    let i = 0;
    let buf = "";
    while (i < flat.length) {
      if (flat[i] === "<") {
        if (buf) {
          tokens.push({
            type: "text",
            value: buf
          });
          buf = "";
        }
        const close = flat.indexOf(">", i);
        if (close === -1) {
          buf += flat.slice(i);
          break;
        }
        tokens.push({
          type: "tag",
          value: flat.slice(i, close + 1)
        });
        i = close + 1;
        continue;
      }
      if (/\s/.test(flat[i])) {
        if (buf) {
          tokens.push({
            type: "text",
            value: buf
          });
          buf = "";
        }
        tokens.push({
          type: "space",
          value: " "
        });
        while (i < flat.length && /\s/.test(flat[i])) {
          i++;
        }
        continue;
      }
      buf += flat[i];
      i++;
    }
    if (buf) {
      tokens.push({
        type: "text",
        value: buf
      });
    }
    const words = [];
    let word = [];
    for (const tok of tokens) {
      if (tok.type === "space") {
        if (word.length) {
          words.push(word);
          word = [];
        }
      } else {
        word.push(tok);
      }
    }
    if (word.length) {
      words.push(word);
    }
    const wordPlain = w => w.filter(t => t.type === "text").map(t => t.value).join("");
    const isSentenceEnd = w => /[.?!]["')\]]*$/.test(wordPlain(w));

    /* Track running color across words (tags may sit on earlier words only) */
    let runColor = null;
    const wordsWithColor = words.map(w => {
      let active = runColor;
      let textColor = null;
      for (const t of w) {
        if (t.type !== "tag") {
          if (t.type === "text" && t.value) {
            textColor = active;
          }
          continue;
        }
        const open = t.value.match(/^<(black|red|green|yellow|blue|magenta|cyan|white)>$/i);
        const close = t.value.match(/^<\/(black|red|green|yellow|blue|magenta|cyan|white)>$/i);
        if (open) {
          active = open[1].toLowerCase();
        }
        if (close && active === close[1].toLowerCase()) {
          active = null;
        }
      }
      runColor = active;
      return {
        tokens: w,
        color: textColor
      };
    });
    const renderWord = (wc, openColor) => {
      let out = "";
      if (wc.color && wc.color !== openColor) {
        if (openColor) {
          out += `</${openColor}>`;
        }
        out += `<${wc.color}>`;
        openColor = wc.color;
      }
      out += wc.tokens.filter(t => t.type === "text").map(t => t.value).join("");
      return {
        out,
        openColor
      };
    };
    const lines = [];
    let lineParts = [];
    let linePlainLen = 0;
    let lineColor = null;
    const flushLine = () => {
      if (!lineParts.length) {
        return;
      }
      let line = lineParts.join("");
      if (lineColor) {
        line += `</${lineColor}>`;
      }
      lines.push(line.trim());
      lineParts = [];
      linePlainLen = 0;
      lineColor = null;
    };
    for (let wi = 0; wi < wordsWithColor.length; wi++) {
      const wc = wordsWithColor[wi];
      const wPlain = wordPlain(wc.tokens);
      const addLen = (linePlainLen === 0 ? 0 : 1) + wPlain.length;
      if (linePlainLen > 0 && linePlainLen + addLen > maxWidth) {
        flushLine();
      }
      if (linePlainLen > 0) {
        lineParts.push(" ");
        linePlainLen += 1;
      }
      const rendered = renderWord(wc, lineColor);
      lineParts.push(rendered.out);
      lineColor = rendered.openColor;
      linePlainLen += wPlain.length;
      if (isSentenceEnd(wc.tokens) && wi + 1 < wordsWithColor.length) {
        const nextLen = wordPlain(wordsWithColor[wi + 1].tokens).length;
        if (linePlainLen + 1 + nextLen > maxWidth) {
          flushLine();
        }
      }
    }
    flushLine();

    /* Prefer first-sentence break onto line 1 when it fits */
    const rebuilt = [];
    lineParts = [];
    linePlainLen = 0;
    lineColor = null;
    let broken = false;
    const flushRebuilt = () => {
      if (!lineParts.length) {
        return;
      }
      let line = lineParts.join("");
      if (lineColor) {
        line += `</${lineColor}>`;
      }
      rebuilt.push(line.trim());
      lineParts = [];
      linePlainLen = 0;
      lineColor = null;
    };
    for (let wi = 0; wi < wordsWithColor.length; wi++) {
      const wc = wordsWithColor[wi];
      const wPlain = wordPlain(wc.tokens);
      const need = (linePlainLen === 0 ? 0 : 1) + wPlain.length;
      if (linePlainLen > 0 && linePlainLen + need > maxWidth) {
        flushRebuilt();
      }
      if (linePlainLen > 0) {
        lineParts.push(" ");
        linePlainLen += 1;
      }
      const rendered = renderWord(wc, lineColor);
      lineParts.push(rendered.out);
      lineColor = rendered.openColor;
      linePlainLen += wPlain.length;
      if (!broken && isSentenceEnd(wc.tokens) && rebuilt.length === 0 && linePlainLen <= maxWidth) {
        const restPlain = wordsWithColor.slice(wi + 1).map(x => wordPlain(x.tokens)).join(" ");
        if (restPlain.length > 0) {
          flushRebuilt();
          broken = true;
        }
      }
    }
    flushRebuilt();
    const candidate = (broken && rebuilt.length ? rebuilt : lines).filter(l => this.toPlainMultiline(l).length > 0);
    if (candidate.length <= maxLines) {
      return candidate.join("\n");
    }
    /* Never drop words: fold overflow onto the last allowed line */
    const kept = candidate.slice(0, maxLines - 1);
    const overflow = candidate.slice(maxLines - 1).join(" ");
    kept.push(overflow);
    return kept.join("\n");
  },
  /**
   * Post-process decoded teletext events: strip scroll carry-over, drop empties,
   * enforce minimum on-screen duration without overlaps, cap at 2 lines.
   * Preserves color tags through cleanup; convertToHtml runs last.
   *
   * When a min-duration extend would hit the next cue, clamp end to next.start
   * instead of delaying later cues (preserves stream sync).
   */
  cleanupTeletextEvents: function (events, options = {}) {
    const minDuration = options.minDuration != null ? options.minDuration : 0.5;
    const maxLines = options.maxLines != null ? options.maxLines : 2;
    const maxWidth = options.maxWidth != null ? options.maxWidth : 38;
    const convertToHtml = options.convertToHtml || (t => t);
    if (!events || !events.length) {
      return events || [];
    }
    const lineCount = text => this.toPlainMultiline(text).split("\n").map(l => l.trim()).filter(Boolean).length;
    const canMergeText = (prevPlain, currPlain) => {
      if (!currPlain || prevPlain.includes(currPlain)) {
        return false;
      }
      return lineCount(prevPlain + "\n" + currPlain) <= maxLines;
    };
    const roundMs = sec => Math.round(sec * 1000) / 1000;

    /* Extend toward minDuration but never past nextStart (keeps stream sync). */
    const clampEnd = (event, nextStart) => {
      let end = event.end;
      if (end - event.start < minDuration - 1e-6) {
        end = roundMs(event.start + minDuration);
      }
      if (nextStart != null && end > nextStart) {
        end = nextStart;
      }
      if (end <= event.start) {
        end = roundMs(event.start + 0.001);
        if (nextStart != null && end > nextStart) {
          end = nextStart;
        }
      }
      event.end = end;
    };
    const result = events.map(e => e);

    /* 1. Strip carry-over prefixes between consecutive cues (keep color tags).
     * If stripping leaves a short remainder that completes an unfinished previous
     * sentence (e.g. "...ask the" + "monkey."), append it to the previous cue
     * instead of orphaning the trailing word(s). */
    for (let i = 1; i < result.length; i++) {
      const beforeStrip = result[i].text;
      result[i].text = this.stripCarryOverTagged(result[i - 1].text, result[i].text);
      const prevPlain = this.toPlainMultiline(result[i - 1].text);
      const remPlain = this.toPlainMultiline(result[i].text);
      if (!remPlain || remPlain === this.toPlainMultiline(beforeStrip)) {
        continue;
      }
      const prevEndsSentence = /[.?!]["')\]]*$/.test(prevPlain);
      const remWords = remPlain.split(/\s+/).filter(Boolean);
      if (!prevEndsSentence && remWords.length > 0 && remWords.length <= 3) {
        const sep = prevPlain.endsWith(" ") || remPlain.startsWith(" ") ? "" : " ";
        result[i - 1].text = (result[i - 1].text || "").replace(/\s+$/, "") + sep + (result[i].text || "").replace(/^\s+/, "");
        result[i - 1].end = Math.max(result[i - 1].end, result[i].end);
        result[i].text = "";
      }
    }

    /* 2. Drop empty events */
    let cleaned = result.filter(event => this.toPlainMultiline(event.text).length > 0);

    /* 3. Merge short cues when possible; otherwise clamp ends (never delay later starts) */
    const merged = [];
    for (let i = 0; i < cleaned.length; i++) {
      const curr = cleaned[i];
      const nextStart = i + 1 < cleaned.length ? cleaned[i + 1].start : null;
      let dur = curr.end - curr.start;
      if (dur < minDuration && merged.length > 0) {
        const prev = merged[merged.length - 1];
        const prevPlain = this.toPlainMultiline(prev.text);
        const currPlain = this.toPlainMultiline(curr.text);
        if (canMergeText(prevPlain, currPlain)) {
          prev.text = this.mergeTaggedLines(prev.text, curr.text, maxLines);
          const prevNext = nextStart;
          prev.end = Math.max(prev.end, curr.end);
          clampEnd(prev, prevNext);
          continue;
        }
        clampEnd(prev, curr.start);
        clampEnd(curr, nextStart);
        merged.push(curr);
        continue;
      }
      if (dur < minDuration && merged.length === 0) {
        const next = i + 1 < cleaned.length ? cleaned[i + 1] : null;
        if (next) {
          const currPlain = this.toPlainMultiline(curr.text);
          const nextPlain = this.toPlainMultiline(next.text);
          if (canMergeText(currPlain, nextPlain)) {
            curr.text = this.mergeTaggedLines(curr.text, next.text, maxLines);
            curr.end = Math.max(curr.end, next.end);
            const afterNext = i + 2 < cleaned.length ? cleaned[i + 2].start : null;
            clampEnd(curr, afterNext);
            cleaned[i + 1] = curr;
            continue;
          }
        }
      }
      clampEnd(curr, nextStart);
      merged.push(curr);
    }

    /* 4. Resolve overlaps: merge if possible, else clamp curr.end to next.start */
    for (let i = 0; i < merged.length; i++) {
      const curr = merged[i];
      const next = i + 1 < merged.length ? merged[i + 1] : null;
      clampEnd(curr, next ? next.start : null);
      if (next && curr.end > next.start) {
        const prevPlain = this.toPlainMultiline(curr.text);
        const nextPlain = this.toPlainMultiline(next.text);
        if (canMergeText(prevPlain, nextPlain)) {
          curr.text = this.mergeTaggedLines(curr.text, next.text, maxLines);
          curr.end = Math.max(curr.end, next.end);
          const afterNext = i + 2 < merged.length ? merged[i + 2].start : null;
          clampEnd(curr, afterNext);
          merged.splice(i + 1, 1);
          i--;
          continue;
        }
        curr.end = next.start;
      }
    }

    /* 4b. Final sweep: clamp only — never shift later cue starts */
    for (let i = 0; i < merged.length; i++) {
      const nextStart = i + 1 < merged.length ? merged[i + 1].start : null;
      clampEnd(merged[i], nextStart);
    }

    /* 5. Inherit color across events, reflow to maxLines, then HTML conversion */
    let carryColor = null;
    for (let i = 0; i < merged.length; i++) {
      if (!this.getLeadingColor(merged[i].text) && carryColor) {
        merged[i].text = this.applyInheritedColor(merged[i].text, carryColor);
      }
      const tagged = this.reflowToMaxLinesTagged(merged[i].text, maxLines, maxWidth);
      const trailing = this.getTrailingColor(tagged);
      if (trailing) {
        carryColor = trailing;
      } else if (!this.getLeadingColor(tagged) && carryColor) {
        /* reflow kept inheritance */
      }
      merged[i].text = tagged ? convertToHtml(tagged) : "";
    }
    return merged.filter(event => this.toPlainMultiline(event.text).length > 0);
  },
  /**
   * Build a debug timeline of on-screen teletext state (no Event open/close logic).
   * One entry per display change on the target page.
   *
   * options: { fieldRate, page, videoFrameRate }
   * Returns: [{ frame, time, fieldIndex, page, erase, rows, display, commands }, ...]
   */
  extractDisplayTimeline: function (input, options = {}) {
    const fieldRate = parseFloat(options.fieldRate) || 50;
    const targetPage = parseInt(options.page != null ? options.page : 888, 10);
    const videoFrameRate = parseFloat(options.videoFrameRate) || 25;
    const packets = this.extractPackets(input, {
      fieldRate
    });
    const timeline = [];
    let currentPage = null;
    let rows = {};
    let lastKey = null;
    const plain = text => this.normalizeText(text);
    const rowMapSnapshot = () => {
      const out = {};
      Object.keys(rows).map(Number).sort((a, b) => a - b).forEach(row => {
        const tagged = rows[row];
        if (plain(tagged)) {
          out[String(row)] = tagged;
        }
      });
      return out;
    };
    const displayLines = rowMap => {
      const lines = [];
      Object.keys(rowMap).map(Number).sort((a, b) => a - b).forEach(row => {
        const tagged = rowMap[String(row)];
        const p = plain(tagged);
        if (!p) {
          return;
        }
        if (lines.length && plain(lines[lines.length - 1]) === p) {
          return;
        }
        lines.push(tagged);
      });
      return lines;
    };
    const pushSnapshot = (pkt, commands, erase) => {
      if (currentPage !== targetPage && !erase) {
        return;
      }
      const rowSnap = rowMapSnapshot();
      const display = displayLines(rowSnap);
      const fieldIndex = Math.round(pkt.time * fieldRate);
      const frame = Math.round(pkt.time * videoFrameRate);
      const key = JSON.stringify({
        erase: !!erase,
        rows: rowSnap,
        commands: commands.map(c => c.type + ":" + (c.row != null ? c.row : "") + ":" + (c.text || ""))
      });
      if (key === lastKey) {
        return;
      }
      lastKey = key;
      timeline.push({
        frame,
        time: Math.round(pkt.time * 1000) / 1000,
        fieldIndex,
        page: targetPage,
        erase: !!erase,
        rows: rowSnap,
        display,
        commands
      });
    };
    for (let i = 0; i < packets.length; i++) {
      const pkt = packets[i];
      if (pkt.row === 0) {
        if (pkt.page === targetPage && pkt.erase === true) {
          rows = {};
          pushSnapshot(pkt, [{
            type: "erase"
          }], true);
        }
        currentPage = pkt.page;
        continue;
      }
      if (currentPage !== targetPage) {
        continue;
      }
      if (pkt.row >= 1 && pkt.row <= 23 && pkt.chars && pkt.chars.length) {
        const lineText = this.charsToText(pkt.chars);
        const commands = [];
        if (plain(lineText)) {
          rows[pkt.row] = lineText;
          commands.push({
            type: "row",
            row: pkt.row,
            text: lineText
          });
        } else {
          if (rows[pkt.row]) {
            commands.push({
              type: "clearRow",
              row: pkt.row
            });
          }
          delete rows[pkt.row];
        }
        if (commands.length) {
          pushSnapshot(pkt, commands, false);
        }
      }
    }
    return timeline;
  },
  /**
   * Convert a display timeline into roll-up Events.
   * Scroll = top-row write whose plain text equals the current bottom row.
   * Window closes when that scroll replaces an existing top line (or on erase/EOF).
   *
   * options: { fieldRate } — used for min end floor only
   * Returns: [{ start, end, text, xPos, yPos, alignment }, ...]
   */
  timelineToEvents: function (timeline, options = {}) {
    const fieldRate = parseFloat(options.fieldRate) || 50;
    const minTick = 1 / fieldRate;
    const events = [];
    let topRow = null;
    let topText = null;
    let bottomRow = null;
    let bottomText = null;
    let windowStart = null;
    let openText = null;
    const plain = text => this.normalizeText(text);
    const composeText = () => {
      const lines = [];
      if (topText && plain(topText)) {
        lines.push(topText);
      }
      if (bottomText && plain(bottomText)) {
        if (!lines.length || plain(lines[lines.length - 1]) !== plain(bottomText)) {
          lines.push(bottomText);
        }
      }
      return lines.join("\n");
    };
    const closeWindow = time => {
      const text = openText || composeText();
      if (windowStart == null || !plain(text)) {
        windowStart = null;
        openText = null;
        return;
      }
      let end = time;
      if (end <= windowStart) {
        end = windowStart + minTick;
      }
      events.push({
        start: windowStart,
        end,
        text,
        xPos: "center",
        yPos: "end",
        alignment: "center"
      });
      windowStart = null;
      openText = null;
    };
    const ensureOpen = time => {
      if (windowStart == null) {
        windowStart = time;
      }
      openText = composeText();
    };
    const clearDisplay = () => {
      topRow = null;
      topText = null;
      bottomRow = null;
      bottomText = null;
    };
    for (let i = 0; i < timeline.length; i++) {
      const snap = timeline[i];
      const time = snap.time;
      for (const cmd of snap.commands || []) {
        if (cmd.type === "erase") {
          closeWindow(time);
          clearDisplay();
          continue;
        }
        if (cmd.type === "clearRow") {
          if (bottomRow === cmd.row) {
            bottomRow = null;
            bottomText = null;
          }
          if (topRow === cmd.row) {
            topRow = null;
            topText = null;
          }
          if (!plain(composeText())) {
            closeWindow(time);
            clearDisplay();
          } else if (windowStart != null) {
            openText = composeText();
          }
          continue;
        }
        if (cmd.type !== "row" || cmd.row == null) {
          continue;
        }
        const row = cmd.row;
        const text = cmd.text || "";
        if (!plain(text)) {
          continue;
        }

        /* Scroll: write to a row above the bottom whose text matches bottom */
        const isScroll = bottomRow != null && bottomText != null && row < bottomRow && plain(text) === plain(bottomText);
        if (isScroll) {
          if (topText && plain(topText)) {
            /* Top already occupied — this scroll pushes it off: window boundary */
            openText = composeText();
            closeWindow(time);
            topRow = row;
            topText = text;
            bottomRow = null;
            bottomText = null;
          } else {
            /* First scroll: establish top line, keep same window */
            topRow = row;
            topText = text;
            if (windowStart != null) {
              openText = composeText();
            }
          }
          continue;
        }

        /* Bottom-row (or sole-row) paint */
        if (bottomRow == null || row === bottomRow) {
          bottomRow = row;
          bottomText = text;
          ensureOpen(time);
          continue;
        }

        /* Writing below current sole/bottom line: promote it to top */
        if (row > bottomRow) {
          topRow = bottomRow;
          topText = bottomText;
          bottomRow = row;
          bottomText = text;
          ensureOpen(time);
          continue;
        }

        /* Write above bottom that is NOT a scroll match */
        if (row < bottomRow) {
          if (topRow != null && row === topRow) {
            const prevTop = plain(topText);
            const nextTop = plain(text);
            /*
             * Same top row, unrelated new text (e.g. next story title while
             * bottom still holds the previous cue's last word). Close the
             * current window first so trailing words are not lost.
             */
            if (prevTop && nextTop && prevTop !== nextTop && !nextTop.startsWith(prevTop) && !prevTop.startsWith(nextTop)) {
              openText = composeText();
              closeWindow(time);
              topRow = row;
              topText = text;
              ensureOpen(time);
              continue;
            }
            topText = text;
            if (windowStart != null) {
              openText = composeText();
            } else {
              ensureOpen(time);
            }
            continue;
          }
          if (topText && plain(topText) && plain(text) !== plain(topText)) {
            openText = composeText();
            closeWindow(time);
          }
          topRow = row;
          topText = text;
          if (windowStart == null) {
            ensureOpen(time);
          } else {
            openText = composeText();
          }
        }
      }
    }
    if (windowStart != null) {
      const lastTime = timeline.length ? timeline[timeline.length - 1].time : windowStart + minTick;
      closeWindow(lastTime);
    }
    return events;
  }
};